import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Loader2, Terminal } from "lucide-react";
import { Card, CardBody, CardFooter, CardHeader } from "@heroui/card";
import { Input } from "@heroui/input";

import {
  resetPasswordSchema,
  ResetPasswordSchema,
} from "@/models/reset-password";
import { apiClient } from "@/api/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";

export default function ResetForm() {
  const { resetToken } = useParams<{ resetToken: string }>();
  const [isSuccess, setIsSuccess] = React.useState(false);

  const form = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ["resetPassword"],
    mutationFn: async (data: ResetPasswordSchema) => {
      await apiClient.post(`/resetPassword/${resetToken}`, {
        newPassword: data.newPassword,
      });
    },
    onSuccess: () => {
      setIsSuccess(true);
      toast.success("Password reset successfully! You can now log in.");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message ||
        "Failed to reset password. The link may be invalid or expired.";
      toast.error(msg);
    },
  });

  function onSubmit(data: ResetPasswordSchema) {
    mutate(data);
  }

  return (
    <div className="container flex min-h-[calc(100vh-80px)] w-full flex-col items-center justify-center py-8">
      {isSuccess && (
        <Alert className="mb-4 w-full max-w-md animate-in fade-in-50">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Success!</AlertTitle>
          <AlertDescription className="flex flex-row items-center gap-2">
            <span>You have successfully reset your password.</span>
            <Button asChild variant="link">
              <Link to="/login">Go to Login Page</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="w-full max-w-md">
        <CardHeader>
          <span>Reset Password</span>
          <br />
          {/*<span>Enter your new password below.</span>*/}
        </CardHeader>
        <CardBody>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your new password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Password must be at least 8 characters, include an
                      uppercase letter and a symbol.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Confirm your new password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardBody>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={() => form.reset()}>
            Reset
          </Button>
          <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
